import { useCallback, useEffect, useMemo } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import BigNumber from 'bignumber.js';

import { useGetStakeTokenBalanceQuery } from 'modules/api/actions/getStakeTokenBalance';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import {
  DEFAULT_LONG_DECIMAL_PLACES,
  HUNDRED,
  MAX_DECIMAL_PLACES,
  ONE,
  ZERO,
} from 'modules/common/const';
import { useGlobalTranslation } from 'modules/i18n';
import { useGetFlashUnstakeDataQuery } from 'modules/liquidStaking/actions/getFlashUnstakeData';
import { UnstakeType } from 'modules/liquidStaking/const';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';

interface IUseStakeFormValues extends FieldValues {
  amount: string;
}

interface IUseUnstakeFormResult {
  amount: BigNumber;
  balance: BigNumber;
  minAmount?: BigNumber;
  receiveAmount: BigNumber;
  ratio: BigNumber;
  receiveForOneAmount: BigNumber;
  onSubmit: (handleSubmit: (amount: BigNumber) => void) => () => void;
  isSubmitDisabled: boolean;
  onMaxClick?: () => void;
  control: Control<IUseStakeFormValues>;
  isError: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
  stakeTokenName: 'stMAT';
}

export function useUnstakeForm(
  unstakeType: UnstakeType,
): IUseUnstakeFormResult {
  const { t, keys } = useGlobalTranslation();
  const { isConnected, connectionKey, cacheTime } = useConnection();

  const { isDeprecated, poolAddress } = useLiquidStakingPool();

  const { data: balance = ZERO } = useGetStakeTokenBalanceQuery(
    { connectionKey, poolAddress },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const { data: flashUnstakeData } = useGetFlashUnstakeDataQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const upToAmount = flashUnstakeData?.upToAmount;

  const { ratio: stakeRatio, name } = useStakeToken(poolAddress);
  const ratio = useMemo(
    () => (stakeRatio.isZero() ? ZERO : ONE.div(stakeRatio)),
    [stakeRatio],
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<IUseStakeFormValues>({
    mode: 'onChange',
    defaultValues: {
      amount: '',
      receiveAmount: '',
    },
  });

  const amountInputValue = watch('amount');
  const convertedAmount = useMemo(() => {
    const convertedValue = new BigNumber(amountInputValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [amountInputValue]);

  const onSubmit = useCallback(
    (onSubmitCallback: (amount: BigNumber) => void) => {
      return handleSubmit(({ amount }) => {
        const convertedAmount = new BigNumber(amount);

        onSubmitCallback(convertedAmount);
      });
    },
    [handleSubmit],
  );

  const onMaxClick = useCallback(() => {
    if (
      upToAmount &&
      unstakeType === UnstakeType.FlashUnstake &&
      upToAmount.isLessThan(balance)
    ) {
      setValue(
        'amount',
        upToAmount.decimalPlaces(MAX_DECIMAL_PLACES).toString(),
        {
          shouldValidate: true,
        },
      );
    } else {
      setValue('amount', balance.decimalPlaces(MAX_DECIMAL_PLACES).toString(), {
        shouldValidate: true,
      });
    }
  }, [balance, setValue, unstakeType, upToAmount]);

  const isAmountError = !!errors.amount;
  const isMoreForFlashUnstake = useMemo(() => {
    if (unstakeType === UnstakeType.FlashUnstake && upToAmount) {
      if (balance.isLessThan(upToAmount)) {
        return false;
      }

      return convertedAmount.isGreaterThan(upToAmount);
    }

    return false;
  }, [balance, convertedAmount, unstakeType, upToAmount]);

  const isSubmitDisabled = useMemo(
    () =>
      isAmountError ||
      isMoreForFlashUnstake ||
      convertedAmount.isZero() ||
      convertedAmount.isGreaterThan(balance),
    [balance, convertedAmount, isAmountError, isMoreForFlashUnstake],
  );

  const calculateReceiveAmount = useCallback(
    (newAmount: BigNumber) => {
      if (unstakeType === UnstakeType.FlashUnstake && flashUnstakeData?.fee) {
        const feeAmount = newAmount.multipliedBy(
          flashUnstakeData.fee.div(HUNDRED),
        );

        return newAmount.minus(feeAmount).multipliedBy(ratio);
      }

      return newAmount.multipliedBy(ratio);
    },
    [flashUnstakeData?.fee, ratio, unstakeType],
  );

  const receiveAmount = useMemo(
    () => calculateReceiveAmount(convertedAmount),
    [calculateReceiveAmount, convertedAmount],
  );

  const receiveForOneAmount = useMemo(
    () => calculateReceiveAmount(ONE),
    [calculateReceiveAmount],
  );

  useEffect(() => {
    setValue(
      'receiveAmount',
      receiveAmount.decimalPlaces(DEFAULT_LONG_DECIMAL_PLACES).toString(),
    );
  }, [receiveAmount, setValue]);

  useEffect(() => {
    if (isDeprecated && balance.isGreaterThan(ZERO)) {
      setValue('amount', balance.decimalPlaces(MAX_DECIMAL_PLACES).toString(), {
        shouldValidate: true,
      });
    }
  }, [balance, isDeprecated, onMaxClick, setValue]);

  return {
    minAmount: isDeprecated ? balance : undefined,
    amount: isDeprecated ? balance : convertedAmount,
    receiveAmount,
    receiveForOneAmount,
    balance,
    onSubmit,
    onMaxClick: isDeprecated ? undefined : onMaxClick,
    isDisabled: isDeprecated,
    isError: isAmountError || isMoreForFlashUnstake,
    errorMessage: isMoreForFlashUnstake
      ? t(keys.errors.maxFlashPoolAmount, { value: upToAmount?.toFixed(2) })
      : undefined,
    control,
    ratio,
    isSubmitDisabled,
    stakeTokenName: name,
  };
}
